import React, { useMemo, useState } from 'react';
import { CSSObject, Theme, useTheme } from '@mui/material/styles';
import { Box, Divider, Drawer, Stack, useMediaQuery } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsEthernet from '@mui/icons-material/SettingsEthernet';
import WidgetsIcon from '@mui/icons-material/Widgets';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';

import { User } from '@travelity/api';
import { useMediaDevice } from '@travelity/ui';
import { useTranslation } from 'react-i18next';
import { FeedbackWidgetButton } from '@components/feedback-widget';
import { ReactComponent as OrderIcon } from '../../../../assets/order-icon.svg';
import { ReactComponent as ProductIcon } from '../../../../assets/product-icon.svg';
import LogoSection from '../logo-section';
import NavItem from './nav-item/nav-item';
import {
    MultiButton,
    MultiButtonItem,
} from '../../../../components/multi-button';
import { BookingModal } from '../../../../components/booking-modal';
import { TeamModal } from '../../../../components/team-modal';

type SidebarProps = {
    user?: User;
    drawerOpen: boolean;
    drawerToggle: (state: boolean) => void;
};

const openedMixin = (theme: Theme): CSSObject => ({
    width: 224,
    transition: theme.transitions.create(['width', 'opacity'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    opacity: 1,
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create(['width', 'opacity'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    opacity: 0,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

function Sidebar({ drawerOpen, drawerToggle, user }: SidebarProps) {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));

    const [bookingOpen, setBookingOpen] = useState(false);
    const [teamOpen, setTeamOpen] = useState(false);
    const { t } = useTranslation();

    const addResourceList = useMemo(() => {
        const list: MultiButtonItem[] = [];
        const resources = user?.visibleResources || [];

        if (user?.roleAccess.booking?.create) {
            list.push({
                label: t('New Booking'),
                Icon: CalendarMonthIcon,
                // path: '/booking/add',
                onClick: () => {
                    drawerToggle(false);
                    setBookingOpen(true);
                },
            });
        }
        if (user?.roleAccess.product?.create) {
            list.push({
                label: t('New Product'),
                nativeIcon: true,
                Icon: ProductIcon,
                onClick: () => {
                    drawerToggle(false);
                },
                path: '/products/add',
            });
        }
        // if (resources.includes('route')) {
        //     list.push({
        //         label: 'New Route',
        //         Icon: DirectionsRoundedIcon,
        //         path: '/route/add',
        //     });
        // }
        // if (resources.includes('customer')) {
        //     list.push({
        //         label: 'New Customer',
        //         Icon: PersonPinRoundedIcon,
        //         path: '/customer/add',
        //     });
        // }
        if (user?.roleAccess.team?.create || !resources.includes('team')) {
            list.push({
                label: t('New Team'),
                Icon: PersonAddAlt1Icon,
                onClick: () => {
                    drawerToggle(false);
                    setTeamOpen(true);
                },
                disabled: !resources.includes('team'),
            });
        }

        return list;
    }, [user?.visibleResources, user?.roleAccess, t]);

    const { isMobile } = useMediaDevice();

    const showAvailability = user?.roleAccess.booking?.create && !isMobile;
    const showEvent = user?.roleAccess.event?.read;
    const showScheduling = showAvailability || showEvent;

    const lockAvailability =
        !user?.visibleResources?.includes('availability') && false;
    const lockEvent = !user?.visibleResources?.includes('event');

    const showDashboard = user?.roleAccess.dashboard?.read;
    const showAnalytics = showDashboard;

    const showProduct = user?.roleAccess.product?.read;
    const showOrder = user?.roleAccess.order?.read && !isMobile;
    const showBooking = user?.roleAccess.booking?.read;
    const showResources = showProduct || showOrder || showBooking;

    const lockProduct = !user?.visibleResources?.includes('product');
    const lockOrder = !user?.visibleResources?.includes('order');
    const lockBooking = !user?.visibleResources?.includes('booking');

    const showTeam = user?.roleAccess.team?.read;
    const showCollaboration = showTeam;

    const lockTeam = !user?.visibleResources?.includes('team');

    const showOtas = user?.roleAccess.product_variant?.read;
    const showWidget = user?.roleAccess.product_variant?.read; // Update this when widget permission is added
    const showIntegrations = showOtas || showWidget;

    const showPayments =
        user?.workspaces.selected.role === 'administrator' ||
        user?.workspaces.selected.role === 'owner';

    const { isMobileTablet } = useMediaDevice();
    const onItemClick = useMemo(
        () => (isMobileTablet ? () => drawerToggle(false) : undefined),
        [drawerToggle, isMobileTablet]
    );

    return (
        <Box
            component="nav"
            sx={{
                flexShrink: { md: 0 },
                width: matchUpMd ? 224 : 'auto',
            }}
            aria-label="mailbox folders"
            className="hide-on-print"
        >
            <Drawer
                // container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                // anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                sx={{
                    ...(drawerOpen ? openedMixin(theme) : closedMixin(theme)),
                    '& .MuiDrawer-paper': {
                        ...(drawerOpen
                            ? openedMixin(theme)
                            : closedMixin(theme)),
                        background: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderRadius: 0,
                        // borderRight: 'none',
                        [theme.breakpoints.up('md')]: {
                            height: 'calc(100% - 64px)',
                            top: '64px',
                        },
                    },
                }}
                ModalProps={{ keepMounted: true, sx: { zIndex: 1201 } }}
                color="inherit"
            >
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
                        <LogoSection />
                    </Box>
                </Box>

                <Box>
                    {!!addResourceList.length && !isMobile && (
                        <Box sx={{ mx: 2, my: 3 }}>
                            <MultiButton listItems={addResourceList} />
                        </Box>
                    )}
                    {showScheduling && (
                        <>
                            <Divider
                                sx={{
                                    mx: 2,
                                    '& .MuiDivider-wrapper': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#CACEDC',
                                    },
                                }}
                            >
                                {t('Scheduling')}
                            </Divider>
                            {showAvailability && (
                                <NavItem
                                    url="/availability"
                                    title={t('Availability Calendar')}
                                    icon={CalendarMonthIcon}
                                    pattern="/availability/*"
                                    disabled={lockAvailability}
                                    onClick={onItemClick}
                                />
                            )}
                            {showEvent && (
                                <NavItem
                                    url="/events"
                                    title={t('Daily View')}
                                    icon={TodayIcon}
                                    pattern="/events/*"
                                    disabled={lockEvent}
                                    onClick={onItemClick}
                                />
                            )}
                        </>
                    )}
                    {showResources && (
                        <>
                            <Divider
                                sx={{
                                    mx: 2,
                                    '& .MuiDivider-wrapper': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#CACEDC',
                                    },
                                }}
                            >
                                {t('Resources')}
                            </Divider>
                            {showProduct && (
                                <NavItem
                                    url="/products"
                                    title={t('Products')}
                                    nativeIcon
                                    icon={ProductIcon}
                                    pattern="/products/*"
                                    disabled={lockProduct}
                                    onClick={onItemClick}
                                />
                            )}
                            {showOrder && (
                                <NavItem
                                    url="/orders"
                                    title={t('Orders')}
                                    nativeIcon
                                    icon={OrderIcon}
                                    pattern="/orders/*"
                                    disabled={lockOrder}
                                    onClick={onItemClick}
                                />
                            )}
                            {showBooking && (
                                <NavItem
                                    url="/bookings"
                                    title={t('Bookings')}
                                    icon={ConfirmationNumberIcon}
                                    pattern="/bookings/*"
                                    disabled={lockBooking}
                                    onClick={onItemClick}
                                />
                            )}
                        </>
                    )}
                    {showCollaboration && (
                        <>
                            <Divider
                                sx={{
                                    mx: 2,
                                    '& .MuiDivider-wrapper': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#CACEDC',
                                    },
                                }}
                            >
                                {t('Collaboration')}
                            </Divider>
                            {showTeam && (
                                <NavItem
                                    url="/teams"
                                    title={t('My Teams')}
                                    nativeIcon
                                    icon={ProductIcon}
                                    pattern="/teams/*"
                                    disabled={lockTeam}
                                    onClick={onItemClick}
                                />
                            )}
                        </>
                    )}
                    {showAnalytics && (
                        <>
                            <Divider
                                sx={{
                                    mx: 2,
                                    '& .MuiDivider-wrapper': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#CACEDC',
                                    },
                                }}
                            >
                                {t('Analytics')}
                            </Divider>
                            {showDashboard && (
                                <NavItem
                                    url="/dashboard"
                                    title={t('Sales Insight')}
                                    icon={DashboardIcon}
                                    pattern="/dashboard/*"
                                    onClick={onItemClick}
                                />
                            )}
                        </>
                    )}
                    {showIntegrations && (
                        <>
                            <Divider
                                sx={{
                                    mx: 2,
                                    '& .MuiDivider-wrapper': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#CACEDC',
                                    },
                                }}
                            >
                                {t('Integrations')}
                            </Divider>
                            {showOtas && (
                                <NavItem
                                    url="/otas"
                                    title={t('OTAs')}
                                    icon={SettingsEthernet}
                                    pattern="/otas/*"
                                    onClick={onItemClick}
                                />
                            )}
                            {showWidget && (
                                <NavItem
                                    url="/widget"
                                    title={t('Widget')}
                                    icon={WidgetsIcon}
                                    pattern="/widget"
                                    onClick={onItemClick}
                                />
                            )}
                        </>
                    )}
                    {showPayments && (
                        <>
                            <Divider
                                sx={{
                                    mx: 2,
                                    '& .MuiDivider-wrapper': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#CACEDC',
                                    },
                                }}
                            >
                                {t('Payments')}
                            </Divider>
                            <NavItem
                                url="/payments/ameriabank"
                                title={t('Ameriabank')}
                                icon={PaymentsOutlinedIcon}
                                pattern="/payments/ameriabank"
                                onClick={onItemClick}
                            />
                            <NavItem
                                url="/payments/flitt"
                                title={t('Flitt')}
                                icon={PaymentsOutlinedIcon}
                                pattern="/payments/flitt"
                                onClick={onItemClick}
                            />
                        </>
                    )}
                </Box>

                <Stack
                    flexGrow={2}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ p: 2 }}
                >
                    <FeedbackWidgetButton />
                </Stack>
            </Drawer>
            {bookingOpen && (
                <BookingModal
                    handleCancel={() => setBookingOpen(false)}
                    // handleConfirm={() => {}}
                    open={bookingOpen}
                />
            )}
            {teamOpen && (
                <TeamModal
                    handleCancel={() => {
                        setTeamOpen(false);
                    }}
                    // handleConfirm={() => {}}
                    open={teamOpen}
                />
            )}
        </Box>
    );
}

export default React.memo(Sidebar);
